/**
 * Renderer-side client for the main process transcript feed.
 *
 * Transcript entry arrays never live in GlobalState (that would structured-
 * clone every append to every renderer). Instead this client, while at least
 * one consumer is mounted:
 *
 *   1. subscribes to the {@link TRANSCRIPT_FEED_TOPIC} broadcast — the broker
 *      only delivers deltas to subscribed renderers, so unmounting stops all
 *      transcript IPC to this window, and
 *   2. pulls one full snapshot via {@link TRANSCRIPT_SNAPSHOT_EVENT}, then
 *      applies revisioned deltas. A revision gap (missed delta) triggers a
 *      snapshot re-fetch.
 *
 * In the browser runtime there is no main process; the browser shims still
 * publish the processed entries on a legacy GlobalState key, so we fall back
 * to subscribing to that.
 */
import { GlobalEvents, GlobalState, subscribeBroadcast } from '@highlight/ipc'
import * as Sentry from '@sentry/electron/renderer'
import type { TranscriptEntry } from '@highlight/common'
import {
  TRANSCRIPT_FEED_TOPIC,
  TRANSCRIPT_SNAPSHOT_EVENT,
  type TranscriptFeedDelta,
  type TranscriptFeedSnapshot,
} from '@shared/transcript-feed'

export interface TranscriptBuffers {
  processed: Array<TranscriptEntry>
  provider: Array<TranscriptEntry>
  merged: Array<TranscriptEntry>
}

const SNAPSHOT_RETRY_MS = 2000
/** One transient failure is a startup race; a streak means the transcript
 *  view is stuck painting nothing — that's worth a Sentry report. */
const SNAPSHOT_FAILURES_BEFORE_REPORT = 3

/** Browser transcription is vestigial (not a real feature today): the browser
 *  shims only ever produce processed entries on this legacy key, so the
 *  provider/merged buffers are always empty in the browser runtime. */
const BROWSER_PROCESSED_KEY = 'audio-note-transcript-entries'

function emptyBuffers(): TranscriptBuffers {
  return { processed: [], provider: [], merged: [] }
}

let buffers: TranscriptBuffers = emptyBuffers()
/** Feed revision of `buffers`; null until a snapshot has been applied for the
 *  current subscription (deltas are queued until then). */
let revision: number | null = null
let refCount = 0
let unsubscribeFeed: (() => void) | null = null
let pendingDeltas: Array<TranscriptFeedDelta> = []
let snapshotInFlight = false
let consecutiveSnapshotFailures = 0
const listeners = new Set<() => void>()

function notifyListeners(): void {
  for (const listener of listeners) {
    listener()
  }
}

function snapshotToBuffers(snapshot: TranscriptFeedSnapshot): TranscriptBuffers {
  return {
    processed: snapshot.processed ?? [],
    provider: snapshot.provider ?? [],
    merged: snapshot.merged ?? [],
  }
}

/** Apply one delta. Only the touched keys get new array identities, so jotai
 *  subscribers of untouched buffers don't re-render. */
function applyDelta(delta: TranscriptFeedDelta): void {
  const next = { ...buffers }
  for (const op of delta.ops) {
    next[op.key] = op.kind === 'replace' ? op.entries : next[op.key].concat(op.entries)
  }
  buffers = next
  revision = delta.revision
}

function handleDelta(delta: TranscriptFeedDelta): void {
  if (revision === null || snapshotInFlight) {
    pendingDeltas.push(delta)
    return
  }
  if (delta.revision <= revision) {
    return
  }
  if (delta.revision !== revision + 1) {
    // Missed at least one delta — resync from a fresh snapshot.
    void refreshSnapshot()
    return
  }
  applyDelta(delta)
  notifyListeners()
}

async function refreshSnapshot(): Promise<void> {
  if (snapshotInFlight) {
    return
  }
  snapshotInFlight = true
  let needsResync = false
  try {
    const snapshot = await GlobalEvents.invoke<TranscriptFeedSnapshot>(TRANSCRIPT_SNAPSHOT_EVENT)
    consecutiveSnapshotFailures = 0
    if (refCount === 0 || !snapshot) {
      return
    }
    buffers = snapshotToBuffers(snapshot)
    revision = snapshot.revision
    // Drain deltas that arrived while the snapshot was in flight.
    const queued = pendingDeltas
    pendingDeltas = []
    for (const delta of queued) {
      if (delta.revision <= revision) {
        continue
      }
      if (delta.revision !== revision + 1) {
        needsResync = true
        break
      }
      applyDelta(delta)
    }
    notifyListeners()
  } catch (err) {
    consecutiveSnapshotFailures += 1
    console.error('[transcript-feed] snapshot fetch failed:', err)
    if (consecutiveSnapshotFailures === SNAPSHOT_FAILURES_BEFORE_REPORT) {
      // A failing snapshot means the transcript view renders nothing — report
      // once per streak (not per retry) so Sentry sees it without being spammed.
      Sentry.captureException(err instanceof Error ? err : new Error(`transcript snapshot failed: ${String(err)}`), {
        tags: { module: 'transcript-feed' },
      })
    }
    // Buffered deltas are unusable without a snapshot base, and any previous
    // revision no longer describes a valid sync point. Drop back to the
    // unsynced state (incoming deltas queue) and retry until a snapshot lands.
    revision = null
    pendingDeltas = []
    if (refCount > 0) {
      setTimeout(() => {
        if (refCount > 0 && revision === null) {
          void refreshSnapshot()
        }
      }, SNAPSHOT_RETRY_MS)
    }
  } finally {
    snapshotInFlight = false
  }
  if (needsResync && refCount > 0) {
    void refreshSnapshot()
  }
}

function readBrowserBuffers(): TranscriptBuffers {
  return {
    processed: GlobalState.get<Array<TranscriptEntry>>(BROWSER_PROCESSED_KEY, []),
    provider: [],
    merged: [],
  }
}

function subscribeBrowserKeys(): () => void {
  buffers = readBrowserBuffers()
  const onUpdate = () => {
    buffers = readBrowserBuffers()
    notifyListeners()
  }
  GlobalState.on(BROWSER_PROCESSED_KEY, onUpdate)
  return () => {
    GlobalState.off(BROWSER_PROCESSED_KEY, onUpdate)
  }
}

/** Latest buffers this renderer knows about. Only live while at least one
 *  consumer holds an acquire; may be stale otherwise (use
 *  {@link fetchTranscriptBuffers} for one-shot reads). */
export function peekTranscriptBuffers(): TranscriptBuffers {
  return buffers
}

/**
 * Subscribe to the transcript feed. `listener` fires whenever the buffers
 * change; read them via {@link peekTranscriptBuffers}. Returns a release
 * function — when the last consumer releases, the broadcast subscription is
 * torn down and this renderer receives no further transcript IPC.
 */
export function acquireTranscriptFeed(listener: () => void): () => void {
  listeners.add(listener)
  refCount += 1
  if (refCount === 1) {
    if (__IS_BROWSER__) {
      unsubscribeFeed = subscribeBrowserKeys()
    } else {
      unsubscribeFeed = subscribeBroadcast<TranscriptFeedDelta>(TRANSCRIPT_FEED_TOPIC, handleDelta)
      void refreshSnapshot()
    }
  }
  let released = false
  return () => {
    if (released) {
      return
    }
    released = true
    listeners.delete(listener)
    refCount -= 1
    if (refCount === 0) {
      unsubscribeFeed?.()
      unsubscribeFeed = null
      revision = null
      pendingDeltas = []
      // Clear buffers so a future consumer doesn't paint stale entries
      // (possibly from a cleared session) while its snapshot is in flight.
      buffers = emptyBuffers()
    }
  }
}

/** One-shot read of the current buffers: uses the live feed when active,
 *  otherwise fetches a fresh snapshot without retaining a subscription. */
export async function fetchTranscriptBuffers(): Promise<TranscriptBuffers> {
  if (__IS_BROWSER__) {
    return readBrowserBuffers()
  }
  if (refCount > 0 && revision !== null) {
    return buffers
  }
  const snapshot = await GlobalEvents.invoke<TranscriptFeedSnapshot>(TRANSCRIPT_SNAPSHOT_EVENT)
  if (!snapshot) {
    return buffers
  }
  return snapshotToBuffers(snapshot)
}
